import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stages available per league
const STAGES_BY_LEAGUE = {
  'Junior League': ['Group Stage', 'Finals'],
  'World Cup': ['Group Stage', 'Finals'],
  'Champions League': ['Group Stage', 'Semi Finals', 'Finals'],
  'Icons League': ['Group Stage', 'Semi Finals', 'Playoff', 'Finals'],
};

export default function MatchSchedule({ ageGroup }) {
  const [matches, setMatches] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  const STAGES = STAGES_BY_LEAGUE[ageGroup] || ['Group Stage', 'Finals'];

  useEffect(() => {
    setSelectedStage('all');
    setInitialLoading(true);
  }, [ageGroup]);

  const openMatchDetails = async (match) => {
    setSelectedMatch(match);
    setLoadingPlayers(true);
    try {
      const requests = [];
      if (match.home_team.id) requests.push(axios.get(`${API}/players?team_id=${match.home_team.id}`));
      else requests.push(Promise.resolve({ data: [] }));
      if (match.away_team.id) requests.push(axios.get(`${API}/players?team_id=${match.away_team.id}`));
      else requests.push(Promise.resolve({ data: [] }));
      const [homeRes, awayRes] = await Promise.all(requests);
      setHomePlayers(homeRes.data);
      setAwayPlayers(awayRes.data);
    } catch (e) {
      console.error('Failed to load players', e);
      setHomePlayers([]);
      setAwayPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  };

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const { data } = await axios.get(`${API}/matches?age_group=${ageGroup}`);
        setMatches(data);
      } catch (error) {
        console.error('Failed to fetch matches:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchMatches();
    const interval = setInterval(fetchMatches, 15000);
    return () => clearInterval(interval);
  }, [ageGroup]);

  const filteredMatches = selectedStage === 'all'
    ? matches
    : matches.filter(m => m.stage === selectedStage);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Brussels',
      hour12: false,
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return <span className="live-indicator"><span className="live-dot"></span>LIVE</span>;
      case 'finished':
        return <Badge className="bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/30">Finished</Badge>;
      default:
        return <Badge className="bg-[#0038B8]/20 text-[#0038B8] border-[#0038B8]/30">Scheduled</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="match-schedule">
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="stage-filter-all"
          onClick={() => setSelectedStage('all')}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
            selectedStage === 'all'
              ? 'bg-[#0038B8] text-white'
              : 'bg-[#131C2F] text-[#9BA3AF] hover:text-white border border-white/10'
          }`}
        >
          All Stages
        </button>
        {STAGES.map((stage) => (
          <button
            key={stage}
            data-testid={`stage-filter-${stage.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => setSelectedStage(stage)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              selectedStage === stage
                ? 'bg-[#0038B8] text-white'
                : 'bg-[#131C2F] text-[#9BA3AF] hover:text-white border border-white/10'
            }`}
          >
            {stage}
          </button>
        ))}
      </div>

      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <h3 className="text-2xl font-bold text-white mb-6">Matches - {ageGroup}</h3>
        {initialLoading ? (
          <p className="text-[#9BA3AF]">Loading matches...</p>
        ) : filteredMatches.length === 0 ? (
          <p className="text-[#9BA3AF]">No matches scheduled for this filter.</p>
        ) : (
          <div className="grid gap-4">
            {filteredMatches.map((match) => (
              <button
                key={match.id}
                type="button"
                data-testid={`match-card-${match.id}`}
                onClick={() => openMatchDetails(match)}
                className="bg-[#0A0F1C] border border-white/5 rounded-xl p-5 hover:border-[#D4AF37]/30 transition-all text-left w-full cursor-pointer"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">
                      {match.stage}
                    </Badge>
                    {match.pitch && (
                      <Badge className="bg-[#0038B8]/20 text-[#0038B8] border-[#0038B8]/40">
                        Pitch {match.pitch}
                      </Badge>
                    )}
                    {getStatusBadge(match.status)}
                  </div>
                  <div className="flex items-center gap-2 text-[#9BA3AF] text-sm">
                    <Calendar className="w-4 h-4" />
                    {formatDate(match.match_date)}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 text-right">
                    <p className={`font-bold text-lg ${match.home_team.id ? 'text-white' : 'text-[#9BA3AF] italic'}`}>{match.home_team.name}</p>
                  </div>
                  <div className="flex items-center gap-3 px-4">
                    <span className="text-3xl font-bold text-[#D4AF37] font-mono">{match.home_score}</span>
                    <span className="text-[#9BA3AF]">vs</span>
                    <span className="text-3xl font-bold text-[#D4AF37] font-mono">{match.away_score}</span>
                  </div>
                  <div className="flex-1">
                    <p className={`font-bold text-lg ${match.away_team.id ? 'text-white' : 'text-[#9BA3AF] italic'}`}>{match.away_team.name}</p>
                  </div>
                </div>

                {match.scorers && match.scorers.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <p className="text-[#9BA3AF] text-xs uppercase tracking-wider mb-2">Goals</p>
                    <div className="flex flex-wrap gap-2">
                      {match.scorers.map((scorer, idx) => (
                        <span key={idx} className="text-sm text-white bg-[#1E2A42] px-3 py-1 rounded-full">
                          {scorer.player_name} {scorer.minute && `(${scorer.minute}')`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-center gap-1 text-[#0038B8] text-xs font-semibold">
                  <Users className="w-3.5 h-3.5" />
                  Click to view players
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Players Dialog */}
      <Dialog open={!!selectedMatch} onOpenChange={(open) => !open && setSelectedMatch(null)}>
        <DialogContent
          data-testid="match-players-dialog"
          className="bg-[#131C2F] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedMatch?.home_team.name}
              <span className="text-[#D4AF37] mx-3">vs</span>
              {selectedMatch?.away_team.name}
            </DialogTitle>
            <div className="flex gap-2 mt-2 flex-wrap">
              {selectedMatch?.stage && (
                <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">
                  {selectedMatch.stage}
                </Badge>
              )}
              {selectedMatch?.pitch && (
                <Badge className="bg-[#0038B8]/20 text-[#0038B8] border-[#0038B8]/40">
                  Pitch {selectedMatch.pitch}
                </Badge>
              )}
              {selectedMatch && (
                <Badge className="bg-white/10 text-[#9BA3AF] border-white/10">
                  {formatDate(selectedMatch.match_date)}
                </Badge>
              )}
            </div>
          </DialogHeader>
          {loadingPlayers ? (
            <p className="text-[#9BA3AF] py-8 text-center">Loading players...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  <h4 className="text-lg font-bold text-white">{selectedMatch?.home_team.name}</h4>
                  <span className="text-[#9BA3AF] text-sm">({homePlayers.length} players)</span>
                </div>
                {homePlayers.length === 0 ? (
                  <p className="text-[#9BA3AF] italic text-sm">No players listed yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {homePlayers.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between bg-[#0A0F1C] border border-white/5 rounded-lg px-3 py-2"
                      >
                        <span className="text-white text-sm">
                          {p.number && <span className="text-[#D4AF37] font-bold mr-2">#{p.number}</span>}
                          {p.name}
                        </span>
                        {p.goals > 0 && (
                          <span className="text-[#D4AF37] text-xs font-bold">{p.goals}⚽</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  <h4 className="text-lg font-bold text-white">{selectedMatch?.away_team.name}</h4>
                  <span className="text-[#9BA3AF] text-sm">({awayPlayers.length} players)</span>
                </div>
                {awayPlayers.length === 0 ? (
                  <p className="text-[#9BA3AF] italic text-sm">No players listed yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {awayPlayers.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between bg-[#0A0F1C] border border-white/5 rounded-lg px-3 py-2"
                      >
                        <span className="text-white text-sm">
                          {p.number && <span className="text-[#D4AF37] font-bold mr-2">#{p.number}</span>}
                          {p.name}
                        </span>
                        {p.goals > 0 && (
                          <span className="text-[#D4AF37] text-xs font-bold">{p.goals}⚽</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
